// Phase P-3 · Packer parcel detail — state machine + photo upload + damage modal.
import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Camera, CheckCircle2, AlertTriangle,
  X, ZoomIn, Trash2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import RoleGate from "../../components/RoleGate";
import PackerSubNav from "../../components/packer/PackerSubNav";

const norm = (s) => (s === "assigned" ? "pending" : (s || "pending"));

const PILL = {
  pending:     "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-orange-100 text-[#C2410C] border-orange-200",
  done:        "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function relTime(iso) {
  if (!iso) return "—";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-label="Photo viewer"
         data-testid="packer-lightbox"
         onClick={onClose}>
      <button onClick={onClose} aria-label="Close"
              className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
              data-testid="packer-lightbox-close">
        <X className="h-5 w-5"/>
      </button>
      <img src={src} alt="" className="max-w-[95vw] max-h-[85vh] rounded-lg"
           onClick={(e) => e.stopPropagation()}/>
    </div>
  );
}

function DamageModal({ tid, photos, onClose, onDone }) {
  const [desc, setDesc] = useState("");
  const [missing, setMissing] = useState("");
  const [selected, setSelected] = useState({}); // url → bool
  const [busy, setBusy] = useState(false);
  const len = desc.length;
  const valid = len >= 10 && len <= 500;

  const submit = async () => {
    if (!valid) { toast.error("Description must be 10–500 chars"); return; }
    setBusy(true);
    try {
      const body = {
        description: desc,
        missing_items: missing ? missing.split(",").map((s) => s.trim()).filter(Boolean) : [],
        photo_ids: Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
      };
      const r = await api.post(`/packer/parcels/${tid}/damage`, body);
      toast.success(`Damage ticket ${r.data?.ticket_id || ""} created`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create ticket");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-2"
         role="dialog" aria-modal="true" aria-label="Report damage"
         data-testid="packer-damage-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-4 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Outfit'] text-lg font-semibold">Report damage</h3>
          <button onClick={onClose} aria-label="Close" data-testid="packer-damage-close">
            <X className="h-4 w-4"/>
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Description *</label>
            <textarea data-testid="packer-damage-desc"
                      value={desc} onChange={(e) => setDesc(e.target.value)}
                      rows={3}
                      className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
                      placeholder="What's wrong with this parcel?"/>
            <div className={"text-[10px] mt-0.5 text-right " + (valid ? "text-slate-400" : "text-rose-500")}>
              {len}/500 (min 10)
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Missing items (comma-separated)</label>
            <input data-testid="packer-damage-missing"
                   value={missing} onChange={(e) => setMissing(e.target.value)}
                   className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
                   placeholder="seal-tape, label"/>
          </div>
          {photos.length > 0 && (
            <div>
              <label className="block text-xs text-slate-500 mb-1">Attach uploaded photos</label>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p) => {
                  const key = p.url || p.id;
                  const on = !!selected[key];
                  return (
                    <button key={key} type="button"
                            data-testid={`packer-damage-photo-${key.slice(-8)}`}
                            onClick={() => setSelected((s) => ({ ...s, [key]: !on }))}
                            className={"relative h-16 border rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00] " +
                                       (on ? "border-[#FF6B00] ring-2 ring-[#FF6B00]" : "border-slate-200")}>
                      <img src={p.url} alt="" className="h-full w-full object-cover"/>
                      {on && (
                        <span className="absolute top-0.5 right-0.5 bg-[#FF6B00] text-white rounded-full p-0.5">
                          <CheckCircle2 className="h-3 w-3"/>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-slate-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy || !valid}
                  data-testid="packer-damage-submit"
                  className="text-sm px-3 py-1.5 bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 inline animate-spin"/> : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Body() {
  const { trackingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(""); // which action is in-flight
  const [pending, setPending] = useState([]); // client-side preview before upload
  const [lb, setLb] = useState(null);
  const [showDamage, setShowDamage] = useState(false);
  const fileRef = useRef(null);
  const meId = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, me] = await Promise.all([
        api.get(`/packer/parcels/${trackingId}`),
        api.get("/packer/me"),
      ]);
      setData(d.data);
      meId.current = me.data?.user_id;
    } catch (e) {
      setError(e?.response?.status === 404
                ? "Not found or not assigned to you."
                : (e?.response?.data?.detail || e.message || "Failed to load"));
    } finally { setLoading(false); }
  }, [trackingId]);

  useEffect(() => { load(); }, [load]);

  const status = norm(data?.packing_staff?.packing_status);
  const photos = data?.packer_uploads || [];
  // P-3 fix · gate Mark Done on session-fresh photos only (count provided by
  // the backend; reflects ts >= packing_started_at AND uploaded_by == me).
  // Historical photos by the same packer from a prior session must NOT pass.
  const sessionPhotoCount = Number.isFinite(data?.current_session_photo_count)
    ? data.current_session_photo_count
    : 0;

  const doStart = async () => {
    setBusy("start");
    try {
      await api.post(`/packer/parcels/${trackingId}/start`);
      toast.success("Packing started");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start packing");
    } finally { setBusy(""); }
  };

  const doUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((f) => fd.append("files", f, f.name));
      await api.post(`/packer/parcels/${trackingId}/photos`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${fileList.length} photo${fileList.length === 1 ? "" : "s"}`);
      setPending([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(""); }
  };

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    // Preview locally first
    setPending(list.map((f) => ({ name: f.name, preview: URL.createObjectURL(f), file: f })));
  };

  const submitPending = async () => {
    await doUpload(pending.map((p) => p.file));
  };

  const removePending = (idx) => {
    setPending((p) => p.filter((_, i) => i !== idx));
  };

  const doComplete = async () => {
    setBusy("complete");
    try {
      await api.post(`/packer/parcels/${trackingId}/complete`);
      toast.success("Packing complete!");
      navigate("/packer/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete packing");
    } finally { setBusy(""); }
  };

  if (loading) {
    return <div className="max-w-[800px] mx-auto p-6 text-center text-slate-400 text-sm">Loading…</div>;
  }
  if (error) {
    return (
      <div className="max-w-[800px] mx-auto p-6 text-center" data-testid="packer-parcel-error">
        <AlertTriangle className="h-8 w-8 text-rose-500 mx-auto mb-2"/>
        <div className="text-sm text-rose-700 mb-3">{error}</div>
        <Link to="/packer/dashboard"
              data-testid="packer-parcel-back"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF6B00] text-white rounded-lg text-sm">
          <ArrowLeft className="h-4 w-4"/> Back to dashboard
        </Link>
      </div>
    );
  }

  const o = data.order || {};
  const cust = data.customer || {};
  const ps = data.packing_staff || {};

  return (
    <div className="max-w-[800px] mx-auto px-3 py-3 space-y-3 pb-[140px]"
         data-testid="packer-parcel-detail" data-status={status}>
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => navigate("/packer/dashboard")} aria-label="Back"
                data-testid="packer-parcel-back-btn"
                className="text-[#FF6B00] hover:text-[#E55F00] transition-colors">
          <ArrowLeft className="h-5 w-5"/>
        </button>
        <span className="font-mono text-sm text-slate-700">{o.tracking_id}</span>
        <span data-testid="packer-parcel-status-pill"
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border font-medium ${PILL[status]}`}>
          {status === "pending" ? "Pending" : status === "in_progress" ? "In Progress" : "Done"}
        </span>
        {data.micro_hub && (
          <span data-testid="packer-parcel-hub-chip"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border font-medium bg-orange-50 text-[#C2410C] border-orange-200 max-w-[55%]"
                title={data.micro_hub.address || ""}>
            <span className="truncate" data-testid="packer-parcel-hub-name">
              {data.micro_hub.name || "Micro hub"}
            </span>
            <span className="font-mono px-1 py-0.5 rounded bg-white/70 text-[9px] text-slate-600 border border-orange-100"
                  data-testid="packer-parcel-hub-id">
              {data.micro_hub.hub_id}
            </span>
          </span>
        )}
        <button onClick={load} aria-label="Refresh"
                className="ml-auto text-slate-400 hover:text-[#FF6B00]"
                data-testid="packer-parcel-refresh">
          <RefreshCw className="h-4 w-4"/>
        </button>
      </div>

      {/* Customer block */}
      <section className="bg-white border border-slate-200 rounded-xl p-3" data-testid="packer-customer">
        <h3 className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Customer</h3>
        <div className="text-sm text-slate-900 truncate">{cust.name || "—"}</div>
        <div className="text-xs text-slate-600 font-mono">{cust.phone_masked || "—"}</div>
        {cust.drop_city && (
          <div className="text-xs text-slate-500 truncate" title={cust.pickup_address_short || ""}>
            → {cust.drop_city}{cust.drop_state ? `, ${cust.drop_state}` : ""}
          </div>
        )}
        {ps?.assigned_at && (
          <div className="text-[11px] text-slate-400 mt-1">Assigned {relTime(ps.assigned_at)}</div>
        )}
      </section>

      {/* Items */}
      <section className="bg-white border border-slate-200 rounded-xl p-3" data-testid="packer-items">
        <h3 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
          Items · {(o.items || []).length || 1}
        </h3>
        {(o.items || []).length > 0 ? (
          <ul className="text-xs text-slate-700 space-y-1">
            {o.items.map((it, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span className="truncate">{it.name || it.parcel_id || `Item ${i + 1}`}</span>
                <span className="text-slate-500">{it.weight_kg ? `${it.weight_kg} kg` : ""}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-slate-500">{o.package_description || "—"}</p>
        )}
      </section>

      {/* Proof photos */}
      <section className="bg-white border border-slate-200 rounded-xl p-3" data-testid="packer-photos">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] uppercase tracking-wider text-slate-500">
            Packing proof photos
          </h3>
          <span className="text-[10px] text-slate-500" data-testid="packer-photos-count">{photos.length}/8</span>
        </div>
        {photos.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-2">No photos yet.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <button key={(p.id || p.url) + i} type="button"
                      onClick={() => setLb(p.url)}
                      data-testid={`packer-photo-tile-${i}`}
                      className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200 hover:border-[#FF6B00] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]">
                <img src={p.url} alt="" className="absolute inset-0 h-full w-full object-cover"/>
                <span className="absolute top-1 right-1 bg-black/40 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                  <ZoomIn className="h-3 w-3"/>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Pending uploads preview */}
      {pending.length > 0 && (
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="packer-pending-uploads">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[10px] uppercase tracking-wider text-amber-700">
              Ready to upload · {pending.length}
            </h3>
            <button onClick={() => setPending([])}
                    data-testid="packer-pending-clear"
                    className="text-[10px] text-rose-600 hover:underline">Clear all</button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {pending.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-amber-300">
                <img src={p.preview} alt="" className="h-full w-full object-cover"/>
                <button onClick={() => removePending(i)}
                        data-testid={`packer-pending-remove-${i}`}
                        aria-label="Remove"
                        className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5">
                  <Trash2 className="h-3 w-3"/>
                </button>
              </div>
            ))}
          </div>
          <button onClick={submitPending} disabled={busy === "upload"}
                  data-testid="packer-pending-submit"
                  className="mt-2 w-full text-sm px-3 py-2 bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg disabled:opacity-60">
            {busy === "upload" ? <Loader2 className="h-4 w-4 inline animate-spin"/> : `Upload ${pending.length} photo${pending.length === 1 ? "" : "s"}`}
          </button>
        </section>
      )}

      {/* Sticky actions footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 pb-[env(safe-area-inset-bottom)]"
           data-testid="packer-actions-footer">
        <div className="max-w-[800px] mx-auto px-3 py-2.5 flex gap-2">
          {status === "pending" && (
            <button onClick={doStart} disabled={busy === "start"}
                    data-testid="packer-start-btn"
                    className="flex-1 text-sm font-semibold px-3 py-2.5 bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg disabled:opacity-60">
              {busy === "start" ? <Loader2 className="h-4 w-4 inline animate-spin mr-1"/> : null}
              Start Packing
            </button>
          )}
          {status === "in_progress" && (
            <>
              <input ref={fileRef} type="file"
                     accept="image/*"
                     capture="environment"
                     multiple
                     onChange={onFileChange}
                     style={{ display: "none" }}
                     data-testid="packer-photo-input"/>
              <button onClick={() => fileRef.current?.click()}
                      data-testid="packer-add-photo-btn"
                      className="flex-1 text-sm font-semibold px-3 py-2.5 border border-[#FF6B00] text-[#FF6B00] hover:bg-orange-50 rounded-lg inline-flex items-center justify-center gap-1">
                <Camera className="h-4 w-4"/> Add Photo
              </button>
              <button onClick={doComplete}
                      disabled={busy === "complete" || sessionPhotoCount < 1}
                      title={sessionPhotoCount < 1
                              ? "Upload at least one packing-proof photo to mark this parcel done."
                              : ""}
                      data-testid="packer-complete-btn"
                      className="flex-1 text-sm font-semibold px-3 py-2.5 bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                {busy === "complete" ? <Loader2 className="h-4 w-4 animate-spin"/> : <CheckCircle2 className="h-4 w-4"/>}
                Mark Done
              </button>
            </>
          )}
          {status === "done" && (
            <div className="flex-1 text-sm text-center text-emerald-700 py-2.5"
                 data-testid="packer-done-banner">
              ✓ Completed{ps.assigned_at ? ` · ${relTime(o.packed_at || ps.assigned_at)}` : ""}
            </div>
          )}
          <button onClick={() => setShowDamage(true)}
                  data-testid="packer-damage-btn"
                  className="text-sm px-3 py-2.5 border border-rose-300 text-rose-600 hover:bg-rose-50 rounded-lg inline-flex items-center justify-center gap-1">
            <AlertTriangle className="h-4 w-4"/> Damage
          </button>
        </div>
      </div>

      <Lightbox src={lb} onClose={() => setLb(null)}/>
      {showDamage && (
        <DamageModal tid={trackingId}
                     photos={photos}
                     onClose={() => setShowDamage(false)}
                     onDone={load}/>
      )}
    </div>
  );
}

export default function PackerParcelDetail() {
  return (
    <RoleGate allow={["packer_staff"]}>
      <PackerSubNav/>
      <Body/>
    </RoleGate>
  );
}
